<template>
  <div class="component-upload-image" style="width: 100%">
    <el-upload
      multiple
      :action="''"
      list-type="picture"
      :auto-upload="true"
      :before-upload="handleBeforeUpload"
      :limit="limit"
      :accept="acceptTypes"
      :on-error="handleUploadError"
      :on-exceed="handleExceed"
      ref="imageUpload"
      :before-remove="handleDelete"
      :show-file-list="false"
      :headers="{}"
      with-credentials
      :file-list="fileList"
      :on-preview="handlePictureCardPreview"
      :class="{ hide: fileList.length >= limit }"
    >
      <!-- <el-icon class="avatar-uploader-icon"><plus /></el-icon> -->
      <el-button type="primary">{{ $tt('点击上传') }}</el-button>

      <!-- 自定义文件项内容 -->
    </el-upload>

    <!-- 拖拽排序区域 -->
    <div class="sortable-container" v-if="fileList.length > 0 && !fileType.includes('mp3')">
      <draggable v-model="fileList" item-key="name" @end="handleDragEnd" class="sortable-list">
        <template #item="{ element, index }">
          <el-badge :value="index + 1" color="green">
            <div class="sortable-item">
              <div class="sortable-item-thumbnail" v-if="isImage(element.fileType || getFileExtension(element.name))">
                <img :src="element.url" alt="" />
              </div>
              <div class="sortable-item-thumbnail video-thumbnail" v-else-if="isVideo(element.fileType || getFileExtension(element.name))">
                <img :src="`${element.url}?x-oss-process=video/snapshot,t_0,f_jpg,w_800,h_600,m_fast`" alt="" />
              </div>
              <div class="sortable-item-actions">
                <!-- <el-icon class="drag-handle"><Rank /></el-icon> -->
                <el-icon class="drag-handle" @click="handlePictureCardPreview(element)">
                  <ZoomIn v-if="isImage(element.fileType || getFileExtension(element.name))" />
                  <VideoPlay v-else-if="isVideo(element.fileType || getFileExtension(element.name))" />
                </el-icon>
                <el-icon class="drag-handle" @click="handleReupload(element)">
                  <Upload />
                </el-icon>
                <el-icon class="drag-handle" @click="handleDelete(element)">
                  <Delete />
                </el-icon>
              </div>
              <p class="sortable-item-extra">
                {{ element.name?.includes('https') ? element.name.split('/')[element.name.split('/').length - 1] : element.name }}
              </p>
            </div>
          </el-badge>
        </template>
      </draggable>
    </div>
    <div v-if="fileList.length > 0 && fileType.includes('mp3')" style="width: 100%">
      <el-table :data="fileList" border size="small" row-key="url" style="width: 100%">
        <el-table-column type="index" :label="$tt('序号')" width="70"></el-table-column>
        <el-table-column prop="musicName" :label="$tt('音乐名')" width="200">
          <template #default="{ row }">
            <el-input v-model="row.musicName" :placeholder="$tt('请输入音乐名')" maxlength="8" show-word-limit />
          </template>
        </el-table-column>
        <el-table-column prop="categoryIdStr" :label="$tt('音乐分类')" min-width="200">
          <template #default="{ row }">
            <el-select v-model="row.categoryIdStr" :placeholder="$tt('选择音乐分类')" clearable multiple filterable>
              <el-option v-for="dict in options" :key="dict.value" :label="dict.label" :value="dict.value" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$tt('文件名')" min-width="200">
          <template #default="{ row }">
            <p>{{ row.url?.includes('https') ? row.url.split('/')[row.url.split('/').length - 1] : row.url }}</p>
          </template>
        </el-table-column>
        <el-table-column prop="duration" :label="$tt('时长')" width="80">
          <template #default="{ row }">
            <p>{{ row.duration ? row.duration + '秒' : '未知' }}</p>
          </template>
        </el-table-column>
        <el-table-column prop="statusM" :label="$tt('音乐状态')" width="120">
          <template #default="{ row }">
            <el-switch v-model="row.statusM" active-value="1" active-text="启用" inactive-value="0" />
          </template>
        </el-table-column>
        <el-table-column :label="$tt('操作')" width="180" fixed="right">
          <template #default="{ row }">
            <el-button type="text" @click="handlePictureCardPreview(row)">{{ $tt('预览') }}</el-button>
            <el-button type="text" @click="handleReupload(row)">{{ $tt('重新上传') }}</el-button>
            <el-button type="text" @click="handleDelete(row)">{{ $tt('删除') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-space>
      <!-- 上传提示 -->
      <div class="el-upload__tip">
        请上传
        <span> {{ $tt('音频文件大小不超过') }} <b style="color: #f56c6c">10M</b> </span>
        <span>
          格式为 <b style="color: #f56c6c">{{ fileType.join('/') }}</b>
        </span>
        的文件
      </div>
    </el-space>

    <el-dialog v-model="dialogVisible" :title="$tt('预览')" width="600px" append-to-body @close="handleDialogClose">
      <img v-if="isImage(dialogFileType)" :src="dialogImageUrl" style="display: block; max-width: 300px; margin: 0 auto" />
      <video v-else-if="isVideo(dialogFileType)" :src="dialogImageUrl" controls style="display: block; max-width: 300px; margin: 0 auto"></video>
      <audio v-else-if="isAudio(dialogFileType)" :src="dialogImageUrl" controls ref="audioPlayer" style="display: block; max-width: 300px; margin: 0 auto"></audio>
    </el-dialog>
  </div>
</template>

<script setup>
import OSS from 'ali-oss'
import { isExternal } from '@/utils/validate'
import { getAliyun } from '@/api/common'
import { Delete, ZoomIn, Plus, VideoPlay, Upload, Rank } from '@element-plus/icons-vue'
import draggable from 'vuedraggable'
import dayjs from 'dayjs'

const props = defineProps({
  modelValue: [String, Object, Array],
  options: {
    type: Array,
    default: () => [],
  },
  // 图片数量限制
  limit: {
    type: Number,
    default: 5,
  },
  // 图片大小限制(MB)
  fileSize: {
    type: Number,
    default: 10,
  },
  // 视频大小限制(MB)
  videoFileSize: {
    type: Number,
    default: 50,
  },
  // 文件类型, 例如['png', 'jpg', 'jpeg', 'mp4', 'mov']
  fileType: {
    type: Array,
    default: () => ['png', 'jpg'],
  },
  // 是否显示提示
  isShowTip: {
    type: Boolean,
    default: true,
  },
  customTip: {
    type: String,
    default: '',
  },
  fileInfoLimit: {
    type: Object,
    default: () => ({
      width: 100,
      height: 100,
      isJudge: false,
    }),
  },
})

const { proxy } = getCurrentInstance()
const emit = defineEmits()
const number = ref(0)
const autoSort = ref(false)
const uploadList = ref([])
const dialogImageUrl = ref('')
const durationMillSeconds = ref(0)
const dialogVisible = ref(false)
const dialogFileType = ref('') // 新增：记录当前预览文件的类型
const audioPlayer = ref(null) // 添加音频播放器引用
const baseUrl = import.meta.env.VITE_APP_BASE_API
const uploadImgUrl = ref(import.meta.env.VITE_APP_BASE_API + '/common/upload') // 上传的图片服务器地址
const fileList = ref([])
const ossClient = ref(null)
const preUrl = ref('')
const cdnUrl = ref('')
const showTip = computed(() => props.isShowTip && (props.fileType || props.fileSize || props.videoFileSize))

// Safari兼容：生成accept属性值（需要MIME类型格式）
const acceptTypes = computed(() => {
  if (!props.fileType || props.fileType.length === 0) return ''

  const mimeMap = {
    mp3: 'audio/mpeg,audio/mp3',
    wav: 'audio/wav,audio/wave',
    ogg: 'audio/ogg',
    aac: 'audio/aac,audio/aacp',
    m4a: 'audio/mp4,audio/m4a',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    bmp: 'image/bmp',
  }

  return props.fileType
    .map(type => {
      const ext = type.toLowerCase()
      const mimeTypes = mimeMap[ext] || `audio/${ext},video/${ext},image/${ext}`
      return `${mimeTypes},.${ext}`
    })
    .join(',')
})

// 判断是否为图片
const isImage = fileType => {
  const imageTypes = ['png', 'jpg', 'gif', 'bmp']
  return imageTypes.some(type => fileType?.toLowerCase().includes(type))
}

// 判断是否为视频
const isVideo = fileType => {
  const videoTypes = ['mp4', 'mov', 'avi', 'wmv', 'flv']
  return videoTypes.some(type => fileType?.toLowerCase().includes(type))
}
// 判断是否为音频
const isAudio = fileType => {
  const audioTypes = ['mp3', 'wav', 'ogg', 'aac']
  return audioTypes.some(type => fileType?.toLowerCase().includes(type))
}

// 获取文件扩展名
const getFileExtension = fileName => {
  if (fileName && fileName.lastIndexOf('.') > -1) {
    return fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase()
  }
  return ''
}

watch(
  () => props.modelValue,
  val => {
    if (val) {
      // 首先将值转为数组
      const list = Array.isArray(val) ? val : props.modelValue.split(',')
      // 然后将数组转为对象数组
      fileList.value = list.map(item => {
        if (typeof item === 'string') {
          if (item.indexOf(baseUrl) === -1 && !isExternal(item)) {
            item = { name: item, url: baseUrl + item }
          } else {
            item = { name: item, url: item }
          }
        }
        // 添加文件类型属性
        if (item.name) {
          const extension = getFileExtension(item.name)
          item.fileType = extension
        }
        return item
      })
    } else {
      fileList.value = []
      return []
    }
  },
  { deep: true, immediate: true }
)

const getImageFileInfo = source =>
  new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = e => {
      const data = e.target.result
      // 加载图片获取图片真实宽度和高度
      const image = new Image()
      image.onload = () => {
        const { width } = image
        const { height } = image
        resolve({
          width,
          height,
        })
      }
      image.src = data
    }
    reader.readAsDataURL(source)
  })

const getVideoFileInfo = source =>
  new Promise((resolve, reject) => {
    // Safari兼容性：使用URL.createObjectURL而不是FileReader
    const audioUrl = URL.createObjectURL(source)
    const audio = document.createElement('audio')
    audio.preload = 'metadata'

    let isResolved = false

    // Safari需要loadedmetadata事件而不是oncanplay
    const handleLoadedMetadata = () => {
      if (isResolved) return
      isResolved = true
      clearTimeout(timeout)
      const duration = audio.duration
      URL.revokeObjectURL(audioUrl) // 清理URL对象
      if (isNaN(duration) || !isFinite(duration)) {
        // Safari可能无法获取某些音频格式的时长，返回0而不是失败
        resolve({ duration: 0 })
      } else {
        resolve({
          duration: parseInt(`${duration}`),
        })
      }
    }

    const handleError = () => {
      if (isResolved) return
      isResolved = true
      clearTimeout(timeout)
      URL.revokeObjectURL(audioUrl)
      // Safari兼容：如果无法加载元数据，返回0而不是失败
      resolve({ duration: 0 })
    }

    // 设置超时处理（Safari可能需要更长时间）
    const timeout = setTimeout(() => {
      if (isResolved) return
      isResolved = true
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('error', handleError)
      URL.revokeObjectURL(audioUrl)
      resolve({ duration: 0 })
    }, 10000) // 10秒超时

    audio.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true })
    audio.addEventListener('error', handleError, { once: true })

    audio.src = audioUrl
  })

// 上传前loading加载
const handleBeforeUpload = async file => {
  await initOssClient()
  let isValidFile = false
  let fileExtension = getFileExtension(file.name)
  const isVideoFile = isVideo(fileExtension)
  const isImageFile = isImage(fileExtension)

  if (props.fileType.length) {
    isValidFile = props.fileType.some(type => {
      // Safari兼容：检查MIME类型和文件扩展名
      const mimeType = file.type?.toLowerCase() || ''
      const ext = type.toLowerCase()

      // 检查MIME类型（Safari可能返回不同的MIME类型）
      if (mimeType.includes(ext) || mimeType.includes(`audio/${ext}`) || mimeType.includes(`video/${ext}`)) {
        return true
      }

      // 检查文件扩展名
      if (fileExtension && fileExtension.toLowerCase() === ext) {
        return true
      }

      // Safari特殊处理：mp3可能返回audio/mpeg
      if (ext === 'mp3' && (mimeType === 'audio/mpeg' || mimeType === 'audio/mp3')) {
        return true
      }

      return false
    })
  } else {
    isValidFile = file.type.indexOf('image') > -1 || file.type.indexOf('video') > -1 || file.type.indexOf('audio') > -1
  }

  if (!isValidFile) {
    proxy.$modal.msgError(`文件格式不正确，请上传${props.fileType.join('/')}格式文件!`)
    return false
  }

  if (file.name.includes(',')) {
    proxy.$modal.msgError('文件名不正确，不能包含英文逗号!')
    return false
  }
  // if (props.fileInfoLimit.isJudge && isImageFile) {
  //   const imageInfo = await getImageFileInfo(file);
  //   if (imageInfo.width !== props.fileInfoLimit.width || imageInfo.height !== props.fileInfoLimit.height) {
  //     proxy.$modal.msgError(`图片宽高尺寸应该为： ${props.fileInfoLimit.width} * ${props.fileInfoLimit.height}`);
  //     return false;
  //   }
  // }
  const duration = await getVideoFileInfo(file)

  // 验证音频时长不能小于30秒
  // Safari兼容：如果无法获取时长（duration为0），跳过时长验证
  if (duration.duration && duration.duration > 0 && duration.duration < 30) {
    proxy.$modal.msgError('上传音频时长不能小于30秒!')
    return false
  }

  // 根据文件类型应用不同的大小限制
  // const sizeLimit = isVideoFile ? props.videoFileSize : props.fileSize;
  const fileSize = file.size / 1024 / 1024
  // const fileInfo = await getImageFileInfo(file);
  // console.log('fileInfo', file, fileInfo)
  if (fileSize > props.fileSize) {
    proxy.$modal.msgError(`上传音频大小不能超过 ${props.fileSize}M!`)
    return false
  }

  proxy.$modal.loading(`正在上传音频，请稍候...`)
  number.value++
  console.log('file', file, number)
  try {
    // 生成OSS文件路径
    const fileName = preUrl.value + '/' + new Date().getTime() + '/' + file.name
    // 执行上传
    const result = await ossClient?.value.multipartUpload(fileName, file)
    // 手动触发成功回调
    handleUploadSuccess(
      {
        code: 200,
        url: cdnUrl.value + '/' + result.name,
        fileName: cdnUrl.value + '/' + result.name,
        fileType: fileExtension,
        isVideo: isVideoFile,
        sortValue: file.name,
        ...duration,
      },
      file
    )

    return false // 阻止 el-upload 默认上传
  } catch (error) {
    proxy.$modal.msgError('上传失败')
    proxy.$modal.closeLoading()
    return false
  }
}

// 文件个数超出
function handleExceed() {
  proxy.$modal.msgError(`上传文件数量不能超过 ${props.limit} 个!`)
}

// 上传成功回调
function handleUploadSuccess(res, file) {
  if (res.code === 200) {
    const fileExtension = getFileExtension(file.name)

    uploadList.value.push({
      name: res.fileName,
      url: res.url,
      fileType: fileExtension,
      sortValue: res.sortValue,
      duration: res.duration || 0,
      statusM: '1',
    })

    uploadedSuccessfully()
  } else {
    number.value--
    proxy.$modal.closeLoading()
    proxy.$modal.msgError(res.msg)
    proxy.$refs.imageUpload.handleRemove(file)
    uploadedSuccessfully()
  }
}

// 删除图片
function handleDelete(file) {
  const findex = fileList.value.map(f => f.name).indexOf(file.name)
  if (findex > -1 && uploadList.value.length === number.value) {
    fileList.value.splice(findex, 1)
    emit('update:modelValue', listToString(fileList.value))
    return false
  }
}

// 上传结束处理
function uploadedSuccessfully() {
  console.log(121, number.value, uploadList.value)
  if (number.value > 0 && uploadList.value.length === number.value) {
    const sortList = uploadList.value.sort((a, b) => a.sortValue.localeCompare(b.sortValue))
    fileList.value = fileList.value.filter(f => f.url !== undefined).concat(sortList)
    uploadList.value = []
    number.value = 0
    emit('update:modelValue', listToString(fileList.value))
    proxy.$modal.closeLoading()
  }
}

// 上传失败
function handleUploadError() {
  proxy.$modal.msgError('上传文件失败')
  proxy.$modal.closeLoading()
}

// 预览
function handlePictureCardPreview(file) {
  console.log('fillll', file)
  dialogImageUrl.value = file.url
  dialogFileType.value = file.fileType || getFileExtension(file.name || file.url)
  dialogVisible.value = true
}

// 关闭弹窗时暂停音频
function handleDialogClose() {
  if (audioPlayer.value && !audioPlayer.value.paused) {
    audioPlayer.value.pause()
    audioPlayer.value.currentTime = 0 // 重置播放进度
  }
}

// 对象转成指定字符串分隔
function listToString(list, separator) {
  console.log('list', list)
  return list
  let strs = ''
  separator = separator || ','
  for (let i in list) {
    if (undefined !== list[i].url && list[i].url.indexOf('blob:') !== 0) {
      strs += list[i].url.replace(baseUrl, '') + separator
    }
  }
  return strs != '' ? strs.substr(0, strs.length - 1) : ''
}
// 获取OSS配置信息
const getOssPolicy = async () => {
  const res = await getAliyun()
  return res.data
}

// 初始化OSS客户端
const initOssClient = async () => {
  if (durationMillSeconds.value && dayjs(durationMillSeconds.value).diff(dayjs(), 'minute') >= 5) {
    return
  }
  const policy = await getOssPolicy()
  durationMillSeconds.value = policy?.durationMillSeconds || dayjs().add(60, 'minute').valueOf()
  preUrl.value = policy?.preUrl
  cdnUrl.value = policy?.cdnUrl
  ossClient.value = new OSS({
    region: 'oss-accelerate',
    accessKeyId: policy?.credentials?.accessKeyId,
    accessKeySecret: policy?.credentials?.accessKeySecret,
    stsToken: policy?.credentials?.securityToken,
    bucket: policy?.bucketName,
    refreshSTSToken: async () => {
      // 调用后端接口获取新的STS Token
      const res = await getAliyun()
      return {
        accessKeyId: res?.data?.credentials?.accessKeyId,
        accessKeySecret: res?.data?.credentials?.accessKeySecret,
        stsToken: res?.data?.credentials?.securityToken,
      }
    },
    refreshSTSTokenInterval: 30 * 60 * 1000, // 30分钟刷新一次
  })
}
initOssClient()

// 处理拖拽结束
const handleDragEnd = () => {
  emit('update:modelValue', listToString(fileList.value))
}

// 处理重新上传
const handleReupload = async file => {
  const input = document.createElement('input')
  input.type = 'file'
  // Safari兼容：需要MIME类型格式，同时保留扩展名格式
  const acceptTypes = props.fileType.map(type => {
    const ext = type.toLowerCase()
    // 为常见音频格式添加MIME类型
    const mimeMap = {
      mp3: 'audio/mpeg,audio/mp3',
      wav: 'audio/wav,audio/wave',
      ogg: 'audio/ogg',
      aac: 'audio/aac,audio/aacp',
      m4a: 'audio/mp4,audio/m4a',
    }
    return mimeMap[ext] ? `${mimeMap[ext]},.${ext}` : `.${ext}`
  }).join(',')
  input.accept = acceptTypes
  input.onchange = async e => {
    const newFile = e.target.files[0]
    if (newFile) {
      const fileExtension = getFileExtension(newFile.name)
      const isVideoFile = isVideo(fileExtension)

      // 检查文件类型
      if (!props.fileType.some(type => fileExtension?.toLowerCase().includes(type))) {
        proxy.$modal.msgError(`文件格式不正确，请上传${props.fileType.join('/')}格式文件!`)
        return
      }

      // 检查文件大小
      // const sizeLimit = isVideoFile ? props.videoFileSize : props.fileSize;
      const fileSize = newFile.size / 1024 / 1024
      if (fileSize > props.fileSize) {
        proxy.$modal.msgError(`上传音频大小不能超过 ${props.fileSize}M!`)
        return
      }

      // 验证音频时长不能小于30秒
      const duration = await getVideoFileInfo(newFile)
      // Safari兼容：如果无法获取时长（duration为0），跳过时长验证
      if (duration.duration && duration.duration > 0 && duration.duration < 30) {
        proxy.$modal.msgError('上传音频时长不能小于30秒!')
        return
      }

      proxy.$modal.loading(`正在上传音频，请稍候...`)

      try {
        await initOssClient()
        // 生成OSS文件路径
        const fileName = preUrl.value + '/' + new Date().getTime() + '/' + newFile.name
        // 执行上传
        const result = await ossClient?.value.multipartUpload(fileName, newFile)

        // 更新文件列表
        const index = fileList.value.findIndex(f => f.name === file.name)
        if (index !== -1) {
          fileList.value[index] = {
            name: cdnUrl.value + '/' + result.name,
            url: cdnUrl.value + '/' + result.name,
            fileType: fileExtension,
            ...duration,
          }
          emit('update:modelValue', listToString(fileList.value))
        }

        proxy.$modal.closeLoading()
      } catch (error) {
        console.log('error', error)
        proxy.$modal.msgError('上传失败')
        proxy.$modal.closeLoading()
      }
    }
  }
  input.click()
}
</script>

<style scoped lang="scss">
// .el-upload--picture-card 控制加号部分
:deep(.hide .el-upload--picture-card) {
  display: none;
}

// 视频预览相关样式
:deep(.el-dialog__body) {
  text-align: center;

  video {
    max-width: 100%;
    max-height: 70vh;
  }
}

// 视频缩略图样式
:deep(.video-thumbnail) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #2c3e50;

  .video-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);

    .video-icon {
      font-size: 24px;
      color: #fff;
    }
  }
}

:deep(.el-upload-list--picture-card .el-upload-list__item-thumbnail) {
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.sortable-container {
  margin-top: 20px;
}

.sortable-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sortable-item {
  position: relative;
  width: 148px;
  height: 177px;
  border: 1px solid #d9d9d9;
  box-sizing: border-box;
  border-radius: 6px;

  // overflow: hidden;
  // cursor: move;
  &:hover {
    border-color: var(--domi-primary-border);

    .sortable-item-actions {
      opacity: 1;
    }
  }
}

.sortable-item-thumbnail {
  width: 148px;
  height: 148px;

  img {
    width: 148px;
    height: 148px;
    object-fit: cover;
  }
}

.sortable-item-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 148px;
  height: 148px;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;

  .drag-handle {
    color: #fff;
    font-size: 20px;

    &:nth-child(even) {
      margin-left: 10px;
      margin-right: 10px;
    }
  }
}

.sortable-item-extra {
  width: 100%;
  height: 25px;
  text-align: center;
  line-height: 25px;
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

:deep(.el-upload-list__item-actions) {
  .el-upload-list__item-reupload {
    margin: 0 5px;
    cursor: pointer;

    &:hover {
      color: var(--domi-primary-text);
    }
  }
}
</style>
